import React, { memo, useCallback } from 'react';
import { Modal, message, Button, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { useNavigate } from 'react-router-dom';
import { GroupingPayload } from '../NewGroupingModal';

const { confirm } = Modal;

interface AdminActionsGroupingProps {
  mutate: () => void;
  item: GroupingPayload;
  showArchived: boolean;
}

const AdminActionsGrouping = ({
  mutate,
  item,
  showArchived,
}: AdminActionsGroupingProps) => {
  const { t } = useTranslation('groupings');
  const navigate = useNavigate();

  const archiveAccount = useCallback(
    (id: number) =>
      api.patch(`/service-proxy/broker/inventory-groups/${id}/archive`),
    [],
  );

  const showArchiveConfirm = useCallback(
    (groupingId: number, groupingName: any) => {
      confirm({
        title: t('msgs.archiveModal.title'),
        icon: <ExclamationCircleOutlined />,
        content: t('msgs.archiveModal.alert', { groupingName }),
        centered: true,
        okText: t('msgs.archiveModal.yes'),
        cancelText: t('msgs.archiveModal.no'),
        okButtonProps: { type: 'primary', danger: true },
        afterClose: () => document.body.style.removeProperty('overflow'),
        onOk: () =>
          archiveAccount(groupingId)
            .then(() => {
              mutate();
              message.success(t('msgs.archiveModal.successArchiving'));
            })
            .catch(({ response: { status } }) => {
              if (status === 409) {
                message.error(t('msgs.archiveModal.archivingWithDependency'));
              } else {
                message.error(t('msgs.archiveModal.archiving'));
              }
            }),
      });
    },
    [archiveAccount, mutate, t],
  );

  return (
    <>
      <Tooltip title={t('buttons.details')}>
        <Button
          icon={<FileSearchOutlined />}
          disabled={!item?.id}
          onClick={() => {
            navigate(
              `/${ApplicationsConstants.MOT_BUSINESS}/mobile-network/administrative/groupings/${item.id}`,
            );
          }}
        />
      </Tooltip>
      {showArchived && (
        <RoleChecker role={rolesConstants.MANAGE_GROUPINGS}>
          <Tooltip title={t('buttons.archive')}>
            <Button
              icon={<FolderOpenOutlined />}
              danger
              onClick={() => showArchiveConfirm(item.id, item.name)}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        </RoleChecker>
      )}
    </>
  );
};

export default memo(AdminActionsGrouping);
